/**
 * The evacuation alert, full screen (FM Technician C20; A12, decision D7).
 *
 * Mounted in AppLayout through EmergencyBanner, for ANY user an active alert
 * reaches — the people in the building today and, since A12, every technician
 * checked in at it. It is the "Are you safe?" answer (Employee rebuild C10,
 * AreYouSafeModal) at full size: the whole screen, two 64 px buttons, word and
 * icon on each — "I am safe at {assembly point}" and "I need help" — because
 * it is read on a phone, on a staircase, in a hurry.
 *
 * Same route as everyone else's answer: POST /emergency/alerts/{id}/respond
 * {status: safe | need_help, location_note}. "I need help" pages the site's
 * FMs and fire wardens at once (evacuation_help_needed). The assembly point is
 * the site's own (alert.assembly_point); with none set the button says "I'm
 * safe" and the line telling people where to gather is left out.
 *
 * ABOVE THE PIN LOCK (FM Technician final review). The app stays mounted
 * under the technician's PIN lock (components/Tech/PinLock, z-[80]), and an
 * evacuation is not private: it must be seen and answered without unlocking.
 * So the overlay sits at z-[90], and it is always the NEWEST dialog —
 * Headless UI makes the newest dialog the top layer and inerts the rest, so
 * the panel is re-mounted whenever the lock goes up or down (the key below):
 * an evacuation already open when the lock comes up stays the one that can be
 * pressed, rather than a red screen above a lock that took its clicks. Once it
 * is closed, the lock is the top dialog again and its keypad answers.
 */
import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { CheckCircleIcon, ExclamationTriangleIcon, LifebuoyIcon } from '@heroicons/react/24/solid';
import { apiPostEnvelope } from '@/utils/api';
import { useAuthStore } from '@/stores/authStore';
import { useTechLocked } from '@/components/Tech/lockState';
import { routeRequestError } from '@/utils/validation';
import { fmTime } from '@/utils/fmTime';
import { orgTimeZone } from '@/utils/timezone';
import type { RespondableAlert } from '@/components/Emergency/AreYouSafeModal';

/** The safe button's words: the site's assembly point when there is one. */
export function safeLabel(assemblyPoint: string | null | undefined): string {
  const ap = assemblyPoint?.trim();
  return ap ? `I am safe at ${ap}` : "I'm safe";
}

export default function EvacuationOverlay({ alert, onClose }: {
  alert: RespondableAlert | null; onClose: () => void;
}) {
  const qc = useQueryClient();
  const locked = useTechLocked(useAuthStore((s) => s.user?.id ?? null));
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState<'safe' | 'need_help' | null>(null);

  const answer = async (status: 'safe' | 'need_help') => {
    if (!alert) return;
    setBusy(status);
    try {
      const r = await apiPostEnvelope<{ status: string }>(`/emergency/alerts/${alert.id}/respond`, {
        status, location_note: note.trim() || null,
      });
      toast.success(r?.message ?? (status === 'safe'
        ? 'Marked safe. Your warden can see it.' : 'Help is coming. Response team alerted.'));
      qc.invalidateQueries({ queryKey: ['emergency'] });
      onClose();
    } catch (e) {
      toast.error(routeRequestError(e, {})?.message ?? 'Your answer was not sent — try again, or call reception.');
    } finally {
      setBusy(null);
    }
  };

  const open = Boolean(alert);
  // "Nearest exit:" — not "from your desk": the overlay reaches technicians
  // checked in at the site, who have no desk, and the exit the server picks
  // is the nearest one it knows for this person.
  const exitLine = alert?.exit_note ? `Nearest exit: ${alert.exit_note}.` : null;
  const gather = alert?.assembly_point ? `Go to the assembly point: ${alert.assembly_point}.` : null;
  // When it was raised, on the BUILDING's clock with the zone named ("15:10
  // IST") — the phone may be on another zone than the site it is at. The
  // organisation's clock only when the alert names no zone.
  const meta = [
    alert?.triggered_by_name ? `Sent by ${alert.triggered_by_name}` : null,
    alert?.triggered_at ? fmTime(alert.triggered_at, alert.timezone || orgTimeZone()) : null,
  ].filter(Boolean).join(' · ');

  return (
    <Transition show={open} as={Fragment}>
      <Dialog key={locked ? 'over-lock' : 'unlocked'} onClose={onClose} className="relative z-[90]">
        <Dialog.Panel className="tech fixed inset-0 overflow-y-auto bg-danger-700 text-white"
                      data-testid="are-you-safe">
          <div className="mx-auto flex min-h-full max-w-lg flex-col justify-center gap-4 p-5" data-testid="evacuation-overlay">
            <p className="flex items-center gap-2 text-base font-bold uppercase tracking-wider">
              <ExclamationTriangleIcon className="h-7 w-7" aria-hidden="true" />
              Emergency{alert?.building_name ? ` · ${alert.building_name}` : ''}
            </p>
            <Dialog.Title className="text-3xl font-bold leading-tight">
              {alert?.type_label}. {alert?.message}
            </Dialog.Title>
            {(exitLine || gather) && (
              <p className="text-lg" data-testid="alert-exit">
                {[exitLine, 'Do not use lifts.', gather].filter(Boolean).join(' ')}
              </p>
            )}
            {meta && <p className="text-sm text-white/85" data-testid="alert-meta">{meta}</p>}
            {alert?.can_respond ? (
              <>
                {alert.my_response && (
                  <p className="text-base text-white/90" data-testid="alert-my-answer">
                    You answered “{alert.my_response === 'safe' ? safeLabel(alert.assembly_point) : 'I need help'}”. You can change it.
                  </p>
                )}
                <div className="space-y-3">
                  <button type="button" disabled={busy !== null} onClick={() => answer('safe')}
                          className="flex min-h-[64px] w-full items-center justify-center gap-3 rounded-2xl bg-white px-4 text-lg font-bold text-success-700 disabled:opacity-60"
                          data-testid="evac-safe">
                    <CheckCircleIcon className="h-7 w-7 shrink-0" aria-hidden="true" />
                    {busy === 'safe' ? 'Sending…' : safeLabel(alert.assembly_point)}
                  </button>
                  <button type="button" disabled={busy !== null} onClick={() => answer('need_help')}
                          className="flex min-h-[64px] w-full items-center justify-center gap-3 rounded-2xl border-4 border-white px-4 text-lg font-bold text-white disabled:opacity-60"
                          data-testid="evac-help">
                    <LifebuoyIcon className="h-7 w-7 shrink-0" aria-hidden="true" />
                    {busy === 'need_help' ? 'Sending…' : 'I need help'}
                  </button>
                </div>
                <label htmlFor="evac-note" className="block text-base font-medium">
                  Where are you? <span className="font-normal text-white/80">(optional)</span>
                </label>
                <input id="evac-note" className="min-h-[48px] w-full rounded-xl border-0 px-3 text-base text-content"
                       maxLength={255} value={note} onChange={(e) => setNote(e.target.value)}
                       placeholder="e.g. Stair 2, level 3" />
                <p className="text-sm text-white/85">Your answer goes to the roll call your fire warden and FM see.</p>
              </>
            ) : (
              <p className="text-base text-white/90">
                This alert is not for a building you are in today, so there is nothing to answer.
              </p>
            )}
            <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
              {alert?.can_view_roll_call ? (
                <Link to={`/safety/roll-call/${alert.id}`} onClick={onClose}
                      className="inline-flex min-h-[48px] items-center text-base font-semibold underline">Open the roll call</Link>
              ) : <span />}
              <button type="button" onClick={onClose}
                      className="min-h-[48px] rounded-xl bg-white/15 px-4 text-base hover:bg-white/25">
                Close
              </button>
            </div>
          </div>
        </Dialog.Panel>
      </Dialog>
    </Transition>
  );
}
